import { BrowserProvider, ethers } from 'ethers';

import { getCurrentStateChainConfig } from 'config/configUtils';
import { KATANA_PERPS, SAVE_STATE_MAX_DELAY_MS, urls } from 'config/constants';
import { isRunningOnInsecureHttp } from 'lib/redirect';
import { staticDispatch } from 'reduxState/store';
import {
  changeWalletChainId,
  updatePendingWalletAddress,
} from 'reduxState/wallet/walletDetailsReducer';

import type { ProviderInterface } from '@coinbase/wallet-sdk';

// https://docs.cloud.coinbase.com/wallet-sdk/docs/initializing
// https://github.com/NoahZinsmeister/web3-react/tree/main/packages/coinbase-wallet

let ethereum: ProviderInterface | undefined;
let provider: BrowserProvider | undefined;

type UnlockReturn =
  | {
      publicKey: string;
      ethereum: typeof ethereum;
      provider: typeof provider;
    }
  | undefined;

const performUnlockCoinbaseWallet = async (): Promise<UnlockReturn> => {
  if (isRunningOnInsecureHttp()) {
    throw new Error('CoinbaseWallet is only working on secure (https) domains');
  }

  const addProviderListeners = () => {
    if (!ethereum) {
      return;
    }
    // Note: "provider.on()" is not working so we use "ethereum.on()"

    // Looks like this event was removed...
    // https://forums.coinbasecloud.dev/t/accountschanged-event-on-wallet-sdk/3726
    ethereum.on('accountsChanged', (accounts: string[]) => {
      console.log('[coinbaseWallet] Account change detected', accounts);
      const firstWallet = accounts[0];
      if (firstWallet) {
        staticDispatch(updatePendingWalletAddress(firstWallet));
      }
    });

    // EIP-1193 specifies hex string, but Coinbase SDK may emit a number in some cases
    ethereum.on('chainChanged', (chainIdRaw: string | number) => {
      if (!chainIdRaw) {
        console.error('[coinbaseWallet] Missing chain id');
        return;
      }
      const chainId = Number(chainIdRaw);
      console.log('[coinbaseWallet] Chain change - rebuilding for', chainId);
      staticDispatch(changeWalletChainId(chainId));
      if (ethereum) {
        // ethereum.removeAllListeners(); // we keep listeners, just reconstruct provider
        provider = new BrowserProvider(ethereum);
      } else {
        console.error('[coinbaseWallet] Eth instance not available');
      }
      return ethereum;
    });
  };

  if (!provider || !ethereum) {
    console.log('[coinbaseWallet] Initialize WalletSDK');
    const CoinbaseWalletSDK = (await import('@coinbase/wallet-sdk')).default;

    // Initialize Coinbase Wallet SDK
    const coinbaseWallet = new CoinbaseWalletSDK({
      appName: KATANA_PERPS,
      appLogoUrl: `${urls.exchange}/static/images/katana-logo-white.svg`,
      // darkMode: true,
      // appChainIds:
    });
    const { chainId, chainRpc } = getCurrentStateChainConfig();
    // TODO: not needed now ?chainRpc, chainId
    ethereum = coinbaseWallet.makeWeb3Provider();

    console.log('[coinbaseWallet] Initialize', {
      chainRpc,
      chainId,
      ethereum,
    });
    provider = new BrowserProvider(ethereum);

    // This trigger is hotfix to "Underlying network changed" error
    // when set app to Polygon chain
    // const activeChain = getActiveChainFromState();
    // const chainConfig = getCurrentStateChainConfig(activeChain);
    // await triggerWalletChainSwitchIfPossible(chainConfig, provider.getSigner());

    // Recreate context now
    // provider.removeAllListeners();
    // await waitAsync(100);
    // provider = new providers.BrowserProvider(ethereum);

    addProviderListeners();
  }

  const walletAddress = await ethereum
    .request({ method: 'eth_requestAccounts' })
    .then(accounts => {
      const firstWallet = (accounts as string[])[0];
      console.log(`[coinbaseWallet] Found wallet ${firstWallet}`);
      return firstWallet;
    });

  // const wallets = await provider.listAccounts();
  // const walletAddress = await wallets[0].getAddress();

  // console.log('[coinbaseWallet] Found wallet', walletAddress);

  // const walletChainId = await detectWalletNetworkChainIdAsync(
  //   {
  //     walletAddress,
  //     walletAddressOriginal: walletAddress,
  //     walletType: 'coinbaseWallet',
  //   },
  //   provider.getSigner()
  // );

  // const activeChain = getActiveChainFromState();
  // const { chainId } = getCurrentStateChainConfig(activeChain);

  // console.log('[coinbaseWallet] Chain id', walletChainId);

  // if (walletChainId && chainId !== walletChainId) {
  //   console.log('[coinbaseWallet] Switching chain id');
  //   await triggerWalletChainSwitchIfPossible(activeChain, provider.getSigner());

  //   // Reset provider otherwise otherwise "Underlying network changed" error
  //   provider.removeAllListeners();
  //   await waitAsync(500);
  //   provider = new providers.BrowserProvider(ethereum);

  //   addProviderListeners();

  //   const wallets = await provider.listAccounts();
  //   walletAddress = wallets[0];
  // }

  // Optionally, have the default account be set automatically when available
  // ethereum.on('accountsChanged', (accounts: string[]) => {
  //   //   web3.eth.defaultAccount = accounts[0];
  // });
  // web3.eth.defaultAccount = web3.eth.accounts[0];

  // https://github.com/NoahZinsmeister/web3-react/blob/main/packages/coinbase-wallet/src/index.ts#L66

  return walletAddress
    ? {
        publicKey: walletAddress,
        ethereum,
        provider,
      }
    : undefined;
};

/** Restricting getSigner to only call unlock once (no parallel runs) */
let unlockingPromise: Promise<UnlockReturn> | undefined;

export const unlockCoinbaseWallet = async (): Promise<UnlockReturn> => {
  unlockingPromise = unlockingPromise
    ? unlockingPromise
    : performUnlockCoinbaseWallet();
  const unlockedWallet = await unlockingPromise;
  unlockingPromise = undefined;
  return unlockedWallet;
};

const removeCoinbaseWalletFromLocalStorage = () => {
  if (ethereum?.disconnect) {
    ethereum?.disconnect();
  }
  // Let's clean up manually
  Object.keys(window.localStorage)
    .filter(
      key =>
        key.includes('__WalletLink__') ||
        key.includes('-coinbaseWallet:') ||
        key.includes('-walletlink:')
    )
    .map(keyToRemove => localStorage.removeItem(keyToRemove));
};

/** We have to wait until wallet state was propagated */
export const disconnectCoinbaseWallet = () => {
  setTimeout(() => {
    // if (ethereum && ethereum.isConnected()) {
    //   ethereum.disconnect();
    // }
    // if (ethereum?.close) {
    //   ethereum?.close();
    // }
    ethereum?.disconnect();

    // Looks like disconnect doesn't remove session
    removeCoinbaseWalletFromLocalStorage();
  }, SAVE_STATE_MAX_DELAY_MS + 10);
};

export const getSigner = async (): Promise<ethers.JsonRpcSigner> => {
  const unlockedWallet = await unlockCoinbaseWallet();

  if (!unlockedWallet?.provider || !unlockedWallet?.ethereum) {
    throw Error('CoinbaseWallet is not available');
  }
  return unlockedWallet.provider.getSigner();
};

export const isCoinbaseApp = (): boolean =>
  (window.ethereum as any)?.isCoinbaseBrowser;
